import dayjs from 'dayjs';
import type {
  StI18nEntry,
  StPoolRewardClaim,
  StPoolRewardClaimFilter,
} from '../../../services/socialTradingService';
import { POOL_REWARD_CLAIM_STATUSES, POOL_REWARD_TARGET_ACCOUNTS } from './constants';

const TEXT_KEYS = ['roomId', 'roomName', 'poolId', 'userId', 'nickname'] as const;
// The cycle-end range has no input on the page. react-admin persists list
// filters, so a value saved before that input was removed would otherwise keep
// filtering the table invisibly — it is dropped here instead.
const DATE_KEYS = ['claimedFrom', 'claimedTo'] as const;

function isOneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}

/**
 * react-admin filter values → the API filter. Blank values are dropped, and so
 * are unknown enum values and unparseable dates: the API answers either with a
 * 400, which would blank the whole page instead of ignoring one bad input.
 */
export function toRewardClaimFilter(values: Record<string, unknown> = {}): StPoolRewardClaimFilter {
  const out: StPoolRewardClaimFilter = {};

  TEXT_KEYS.forEach((key) => {
    const value = values[key];
    if (typeof value === 'string' && value.trim()) out[key] = value.trim();
  });

  const { claimStatus, targetAccount } = values;
  if (isOneOf(POOL_REWARD_CLAIM_STATUSES, claimStatus)) out.claimStatus = claimStatus;
  if (isOneOf(POOL_REWARD_TARGET_ACCOUNTS, targetAccount)) out.targetAccount = targetAccount;

  DATE_KEYS.forEach((key) => {
    const value = values[key];
    if (typeof value === 'string' && value && dayjs(value).isValid()) {
      out[key] = dayjs(value).toISOString();
    }
  });

  return out;
}

/** DateInput `parse` for a range start: the picked day from local midnight. */
export const dayStartIso = (value: string): string | undefined =>
  value ? dayjs(value).startOf('day').toISOString() : undefined;

/** DateInput `parse` for a range end: inclusive of the whole picked day. */
export const dayEndIso = (value: string): string | undefined =>
  value ? dayjs(value).endOf('day').toISOString() : undefined;

/** DateInput `format`: stored ISO timestamp → the `YYYY-MM-DD` the picker shows. */
export const isoToDateInput = (value: unknown): string =>
  typeof value === 'string' && value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';

/**
 * Why the last claim attempt failed, or null.
 *
 * A failed claim falls back to CLAIMABLE with every withdrawal field null, so
 * `lastFailureReason` is the only thing separating "tried and failed" from
 * "never tried".
 */
export function claimFailureReason(
  row: Pick<StPoolRewardClaim, 'claimStatus' | 'lastFailureReason'>,
): string | null {
  if (row.claimStatus !== 'CLAIMABLE') return null;
  return row.lastFailureReason?.trim() || null;
}

/** Cycle label in the admin UI language (UI `cn` ↔ data `zh`), else the default label. */
export function pickCycleLabel(
  label: string,
  entries: StI18nEntry[] | null | undefined,
  uiLocale: string,
): string {
  const wanted = (uiLocale === 'cn' ? 'zh' : uiLocale).toLowerCase();
  const hit = (entries ?? []).find((e) => e.lang?.toLowerCase() === wanted && e.text?.trim());
  return hit ? hit.text : label;
}

const DECIMAL_STRING = /^(-?)(\d+)(\.\d+)?$/;

/** Adds 1 to a non-negative integer written as digits, carrying as far as needed. */
function incrementDigits(digits: string): string {
  let carry = 1;
  const out = digits.split('').reduceRight((acc, ch) => {
    const sum = Number(ch) + carry;
    carry = sum >= 10 ? 1 : 0;
    return `${sum % 10}${acc}`;
  }, '');
  return carry ? `1${out}` : out;
}

/**
 * Rounds `integer.fraction` to `digits` places, half away from zero, working
 * on the digit string — `999.995` → `["1000", "00"]` — and pads a short
 * fraction with zeros.
 */
function roundFraction(integer: string, fraction: string, digits: number): [string, string] {
  if (fraction.length <= digits) return [integer, fraction.padEnd(digits, '0')];
  const kept = `${integer}${fraction.slice(0, digits)}`;
  const rounded = fraction[digits] >= '5' ? incrementDigits(kept) : kept;
  const cut = rounded.length - digits;
  return [rounded.slice(0, cut) || '0', rounded.slice(cut)];
}

/**
 * `"1234567.5"` → `"1,234,567.5"`, or `"1,234,567.50"` with `fractionDigits`
 * set to 2. Done on the string so the value is never turned into a float: a
 * large or long-fraction amount would otherwise lose precision, and
 * reconciling tiles against rows is the point of this page.
 *
 * Without `fractionDigits` the fraction is kept exactly as received. Anything
 * that is not a plain decimal comes back untouched rather than being guessed at.
 */
export function formatDecimalString(value: string, fractionDigits?: number): string {
  const match = DECIMAL_STRING.exec(value.trim());
  if (!match) return value;
  const [, sign, integer, dotFraction = ''] = match;
  const [int, frac] =
    fractionDigits === undefined
      ? [integer, dotFraction.slice(1)]
      : roundFraction(integer, dotFraction.slice(1), fractionDigits);
  // `-0.001` rounds to zero; showing it as `-0.00` would read as a debt.
  const shownSign = /^0*$/.test(`${int}${frac}`) ? '' : sign;
  return `${shownSign}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${frac ? `.${frac}` : ''}`;
}

/** A user count with thousands separators, or an empty-cell dash. */
export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toLocaleString('en-US');

/**
 * `"1234.5"` → `"1,234.50 USDC"`, or null when there is no amount — so a claim
 * that has not happened renders as an empty cell, never as "0". Rounded to two
 * places on the string, same as the KPI tiles; never parsed into a float.
 */
export function formatClaimAmount(amount: string | null | undefined, currency?: string | null): string | null {
  if (amount === null || amount === undefined || amount === '') return null;
  const shown = formatDecimalString(amount, 2);
  return currency ? `${shown} ${currency}` : shown;
}
